import type { TFunction } from "i18next"
import i18n from "@/lib/i18n/i18n"
import { Activity } from "@/reducers/activitySlice"
import { Location } from "@/reducers/locationsSlice"
import { ReferenceRouteStatus } from "@/reducers/referenceRouteStatusesSlice"
import {
  findCurrentTarget,
  isStopActive,
  isStopPending,
  Route2g,
  RouteStop,
  shouldAlert,
} from "@/reducers/routes2gSlice"
import type { AvoidType } from "@/api/adHocCommands"
import { stopAvoidsTolls } from "./routeStopEdits"
import { getDisplayedStopDistance } from "./stopDistance"
import { stopArrivalOf, stopDistanceOf, type StopDistance } from "./stopStatus"
import { formatClockTime, formatDuration, parseWarsawTime } from "./warsawTime"
import { DriverWorkTime } from "./workTime"

export type OverviewInput = {
  route: Route2g | undefined
  activity: Activity | undefined
  referenceRouteStatus: ReferenceRouteStatus | undefined
  syncFinished: boolean
  accountHasTacho: boolean
  workTime: DriverWorkTime | null
  location: Location | undefined
  now: number
  timeZone?: string
  showLegDistance?: boolean
  routeAvoid?: AvoidType[]
  // The caller's `t`, so a language switch re-renders; plain calls use i18n.
  t?: TFunction
}

export type Stage = { text: string; detail?: string }

export type StageDetail = { label: string; value: string; tooltip: string }

export type StopEta =
  | { label: string; tone: "unplanned" | "onTime" }
  | { label: string; tone: "late"; delay: string }

export type OverviewStop = {
  id: string
  action?: string
  name: string
  address?: string
  distance: StopDistance | null
  eta: StopEta | null
  alert: boolean
  current: boolean
  aheadPercent: number | null
  movable?: boolean
  editable?: boolean
  done?: boolean
  avoidTolls: boolean
}

export type RouteView = {
  stage: Stage
  stageDetail: StageDetail | null
  moving: boolean
  status: "onRoute" | "offRoute" | null
  breakMarker: { percent: number; tooltip: string } | null
  stops: OverviewStop[]
}

export type WorkTimeView =
  | { kind: "none" }
  | { kind: "noTacho" }
  | {
      kind: "figures"
      leftLabel: string
      usedPercent: number
      breakLabel: string | null
      breakPercent: number | null
      staleLabel: string | null
    }

export type DriverOverview =
  | { kind: "hidden" }
  | { kind: "syncing" }
  | { kind: "noRoute"; stage: Stage; stageDetail: StageDetail | null }
  | { kind: "route"; route: RouteView; workTime: WorkTimeView }

const LAST_MARKER_PERCENT = 99

const AT_STOP: Record<string, "loading" | "unloading"> = {
  pickup: "loading",
  delivery: "unloading",
  "drop-off": "unloading",
}

const STANDING: Record<string, "break" | "rest" | "stopped"> = {
  break: "break",
  rest: "rest",
  stopped: "stopped",
}

const tOf = (input: OverviewInput): TFunction => input.t ?? i18n.t

type Timing = { seconds: number; meters: number }

const placeOf = (location: Location | undefined): string | null =>
  [location?.postalCode, location?.locality].filter(Boolean).join(" ") || null

const addressOf = (location: Location): string =>
  location.formattedAddress ??
  location.companyName ??
  placeOf(location) ??
  `${location.lat}, ${location.lon}`

const durationLabel = (seconds: number): string => {
  const minutes = Math.round(seconds / 60)
  const hours = Math.floor(minutes / 60)
  return hours > 0 ? `${hours} h ${minutes % 60} min` : `${minutes} min`
}

const hoursAndMinutes = (seconds: number): string => {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`
}

const metersAhead = (stop: RouteStop): number | null =>
  isStopPending(stop) && stop.remaining_distance
    ? stop.remaining_distance
    : null

type StageView = { stage: Stage; stageDetail: StageDetail | null }

// A driver standing outside a stop: where, and from the tacho whether on a
// break or a rest - with how long, when the provider dates the rest.
const standingOf = (input: OverviewInput): StageView | null => {
  const { activity, workTime, location } = input
  const t = tOf(input)
  const place = placeOf(location)
  const stopped = activity !== undefined && activity.specific !== "driving"
  const standingKey =
    STANDING[workTime?.activity ?? ""] ?? (stopped ? STANDING.stopped : null)
  if (!standingKey) return null
  const standing = t(`chat.driverOverview.standing.${standingKey}`)
  const resting = workTime?.resting
  return {
    stage: place
      ? { text: place, detail: `, ${standing.toLowerCase()}` }
      : { text: standing },
    stageDetail: resting
      ? {
          label: t("chat.driverOverview.restTime"),
          value: durationLabel(resting.seconds),
          tooltip: t("chat.driverOverview.restingSince", {
            since: resting.since.label,
          }),
        }
      : null,
  }
}

const stageOf = (target: RouteStop, input: OverviewInput): StageView => {
  const { now, timeZone } = input
  const t = tOf(input)
  if (target.proximity_status === "arrived") {
    const atStop = AT_STOP[target.action ?? ""] ?? "atStop"
    const arrivedAt = parseWarsawTime(target.arrived_at)
    return {
      stage: {
        text: t(`chat.driverOverview.stage.${atStop}`, {
          address: addressOf(target.location),
        }),
      },
      stageDetail:
        arrivedAt === null
          ? null
          : {
              label: t(`chat.driverOverview.stageDetail.${atStop}`),
              value: durationLabel((now - arrivedAt) / 1000),
              tooltip: t("chat.driverOverview.inStopAreaSince", {
                time: formatClockTime(arrivedAt, now, timeZone),
              }),
            },
    }
  }
  return (
    standingOf(input) ?? {
      stage: {
        text: t("chat.driverOverview.enRoute", {
          place: placeOf(target.location) ?? addressOf(target.location),
        }),
      },
      stageDetail: null,
    }
  )
}

const stopEtaOf = (
  stop: RouteStop,
  { now, timeZone }: OverviewInput,
): StopEta | null => {
  const arrival = stopArrivalOf(stop, isStopActive(stop))
  const eta = parseWarsawTime(arrival?.arrival)
  if (!arrival || eta === null) return null
  const label = formatClockTime(eta, now, timeZone)
  const late = arrival.secondsLate
  if (late === null) return { label, tone: "unplanned" }
  if (late <= 0) return { label, tone: "onTime" }
  return { label, tone: "late", delay: durationLabel(late) }
}

const breakMarkerOf = (
  stops: RouteStop[],
  farthestMeters: number,
  input: OverviewInput,
): RouteView["breakMarker"] => {
  const { workTime, now } = input
  const nextBreak = workTime?.today.nextBreak
  if (!nextBreak || farthestMeters <= 0) return null
  const dueInSeconds = (nextBreak.startsAt.at - now) / 1000
  if (dueInSeconds <= 0) return null
  const timings = stops
    .flatMap((stop): Timing[] => {
      const meters = metersAhead(stop)
      return meters === null || stop.remaining_time === undefined
        ? []
        : [{ seconds: stop.remaining_time, meters }]
    })
    .sort((a, b) => a.seconds - b.seconds)
  let previous: Timing = { seconds: 0, meters: 0 }
  for (const timing of timings) {
    if (dueInSeconds <= timing.seconds) {
      const share =
        timing.seconds === previous.seconds
          ? 1
          : (dueInSeconds - previous.seconds) /
            (timing.seconds - previous.seconds)
      const meters = previous.meters + share * (timing.meters - previous.meters)
      return {
        percent: Math.min(LAST_MARKER_PERCENT, (meters / farthestMeters) * 100),
        tooltip: tOf(input)("chat.driverOverview.breakDue", {
          time: nextBreak.startsAt.label,
        }),
      }
    }
    previous = timing
  }
  return null
}

const stopsOf = (
  stops: RouteStop[],
  target: RouteStop,
  farthestMeters: number,
  input: OverviewInput,
): OverviewStop[] =>
  stops.map((stop, index) => {
    const ahead = metersAhead(stop)
    const company = stop.location.companyName
    const alert = shouldAlert(stop)
    return {
      id: stop.id ?? String(index),
      action: stop.action,
      name: company ?? addressOf(stop.location),
      address: company
        ? (stop.location.formattedAddress ??
          placeOf(stop.location) ??
          undefined)
        : undefined,
      distance: stopDistanceOf(
        stop,
        getDisplayedStopDistance(stops, index, input.showLegDistance ?? false),
        { active: isStopActive(stop), target: stop === target, alert },
      ),
      eta: stopEtaOf(stop, input),
      alert,
      current: stop === target,
      movable: isStopActive(stop) || Boolean(stop.is_temporary),
      editable:
        (isStopActive(stop) || Boolean(stop.is_temporary)) &&
        stop.action !== "from_driver_location",
      done: stop.proximity_status !== undefined && !isStopActive(stop),
      avoidTolls: stopAvoidsTolls(stop, input.routeAvoid),
      aheadPercent:
        ahead === null
          ? null
          : Math.min(LAST_MARKER_PERCENT, (ahead / farthestMeters) * 100),
    }
  })

const routeViewOf = (
  stops: RouteStop[],
  target: RouteStop,
  input: OverviewInput,
): RouteView => {
  const { activity, referenceRouteStatus } = input
  const farthestMeters = Math.max(
    0,
    ...stops.map((stop) => metersAhead(stop) ?? 0),
  )
  return {
    ...stageOf(target, input),
    moving: activity === undefined || activity.specific === "driving",
    status: referenceRouteStatus
      ? referenceRouteStatus.isOffRoute
        ? "offRoute"
        : "onRoute"
      : null,
    breakMarker: breakMarkerOf(stops, farthestMeters, input),
    stops: stopsOf(stops, target, farthestMeters, input),
  }
}

const workTimeViewOf = (input: OverviewInput): WorkTimeView => {
  const { accountHasTacho, workTime } = input
  if (!accountHasTacho) return { kind: "none" }
  if (!workTime) return { kind: "noTacho" }
  const { today, freshness } = workTime
  const nextBreak = today.nextBreak
  return {
    kind: "figures",
    leftLabel: hoursAndMinutes(today.leftSeconds),
    usedPercent: today.usedShare * 100,
    breakLabel: nextBreak
      ? [nextBreak.startsAt.label, nextBreak.endsAt?.label]
          .filter(Boolean)
          .join(" - ")
      : null,
    breakPercent: nextBreak ? nextBreak.position * 100 : null,
    staleLabel: freshness?.stale
      ? tOf(input)("chat.driverOverview.updatedAgo", {
          duration: formatDuration(freshness.ageSeconds),
        })
      : null,
  }
}

export const driverOverview = (input: OverviewInput): DriverOverview => {
  const stops = input.route?.stops ?? []
  const target = findCurrentTarget(stops)
  if (target) {
    return {
      kind: "route",
      route: routeViewOf(stops, target, input),
      workTime: workTimeViewOf(input),
    }
  }
  if (!input.syncFinished) return { kind: "syncing" }
  if (!input.workTime) return { kind: "hidden" }
  return {
    kind: "noRoute",
    ...(standingOf(input) ?? {
      stage: {
        text:
          placeOf(input.location) ??
          tOf(input)("chat.driverOverview.noActiveRoute"),
      },
      stageDetail: null,
    }),
  }
}
